// ============================================================================
// KLINISOL CRO - Training Matrix API Service
// File: src/services/trainingMatrixApi.js
// Purpose: API calls for RACI training matrix functionality
// ============================================================================

import api from './api'; // Existing axios instance

const trainingMatrixApi = {
    // ========================================================================
    // FUNCTIONAL ROLES
    // ========================================================================
    
    /**
     * Get all functional roles (CRL, DBL, PM, QAC)
     * @returns {Promise<Array>} List of functional roles
     */
    getFunctionalRoles: async () => {
        try {
            const response = await api.get('/training-matrix/functional-roles');
            return response.data.data;
        } catch (error) {
            console.error('Error fetching functional roles:', error);
            throw error;
        }
    },

    // ========================================================================
    // RACI MATRIX
    // ========================================================================
    
    /**
     * Get full RACI matrix
     * @param {Object} params - Query parameters
     * @param {string} params.category - Optional category filter
     * @returns {Promise<Object>} Matrix data with categories
     */
    getRACIMatrix: async (params = {}) => {
        try {
            const response = await api.get('/training-matrix/raci-matrix', { params });
            return response.data.data;
        } catch (error) {
            console.error('Error fetching RACI matrix:', error);
            throw error;
        }
    },

    // ========================================================================
    // COMPLIANCE
    // ========================================================================
    
    /**
     * Get current user's compliance status
     * @returns {Promise<Object>} User's compliance data
     */
    getMyCompliance: async () => {
        try {
            const response = await api.get('/training-matrix/my-compliance');
            return response.data.data;
        } catch (error) {
            console.error('Error fetching my compliance:', error);
            throw error;
        }
    },

    /**
     * Get compliance summary for all users (Admin only)
     * @returns {Promise<Array>} All users' compliance summary
     */
    getAllUsersComplianceSummary: async () => {
        try {
            const response = await api.get('/training-matrix/compliance-summary');
            return response.data.data;
        } catch (error) {
            console.error('Error fetching compliance summary:', error);
            throw error;
        }
    },

    /**
     * Get specific user's compliance details (Admin only)
     * @param {number} userId - User ID
     * @returns {Promise<Object>} User's detailed compliance
     */
    getUserCompliance: async (userId) => {
        try {
            const response = await api.get(`/training-matrix/users/${userId}/compliance`);
            return response.data.data;
        } catch (error) {
            console.error('Error fetching user compliance:', error);
            throw error;
        }
    },

    // ========================================================================
    // ROLE ASSIGNMENTS
    // ========================================================================
    
    /**
     * Get all functional role assignments (Admin only)
     * @param {Object} params - Query parameters
     * @param {string} params.active - Filter active only (true/false)
     * @returns {Promise<Array>} Role assignments
     */
    getRoleAssignments: async (params = { active: 'true' }) => {
        try {
            const response = await api.get('/training-matrix/role-assignments', { params });
            return response.data.data;
        } catch (error) {
            console.error('Error fetching role assignments:', error);
            throw error;
        }
    },

    /**
     * Assign functional role to user (Admin only)
     * @param {Object} data - Assignment data
     * @param {number} data.userId - User ID
     * @param {number} data.functionalRoleId - Functional role ID
     * @param {string} data.effectiveDate - Effective date (YYYY-MM-DD)
     * @param {string} data.reason - Optional reason
     * @returns {Promise<Object>} Assignment result
     */
    assignFunctionalRole: async (data) => {
        try {
            const response = await api.post('/training-matrix/role-assignments', data);
            return response.data;
        } catch (error) {
            console.error('Error assigning functional role:', error);
            throw error;
        }
    },

    // ========================================================================
    // ALERTS & EXPORT
    // ========================================================================
    
    /**
     * Get training expiring within specified days
     * @param {number} days - Days until expiry (default 30)
     * @returns {Promise<Object>} Expiring training data
     */
    getExpiringTraining: async (days = 30) => {
        try {
            const response = await api.get('/training-matrix/expiring', { 
                params: { days } 
            });
            return response.data.data;
        } catch (error) {
            console.error('Error fetching expiring training:', error);
            throw error;
        }
    },

    /**
     * Export training matrix for audit (Admin only)
     * @param {string} format - Export format (json)
     * @returns {Promise<Object>} Export data
     */
    exportTrainingMatrix: async (format = 'json') => {
        try {
            const response = await api.get('/training-matrix/export', {
                params: { format }
            });
            return response.data.data;
        } catch (error) {
            console.error('Error exporting training matrix:', error);
            throw error;
        }
    }
};

export default trainingMatrixApi;
